import {useHistory} from 'react-router-dom/cjs/react-router-dom.min';
import {useAppContext, AppActions} from '../../Context/ProjectContextV2';
import {useUser} from '../../Context/UserContext';
import {Button, Divider, Input, InputNumber, notification, Popover, Space, Typography} from 'antd';
import GrayButton from './GrayButton';
import SelectPlanV2 from './SelectPlanV2';
import PopoverScrollContent from './PopoverScrollContent';
import ProjectInfo from '../ProjectInfo';
import ResultListBox from '../Output/UI/ResultListBox';
import {width} from 'plotly.js/src/components/errorbars/attributes';
import HecRasHelper from '../../Helpers/HecRasHelper';
import {useEffect, useState} from 'react';
import ApiService from '../../Service/ApiService';
import ResultTable from '../Table/ResultTable';
import ResultHeader from '../Table/ResultHeader';
import ResultRow from '../Table/ResultRow';
import ResultHeaderCell from '../Table/ResultHeaderCell';
import ResultBody from '../Table/ResultBody';
import ResultBodyCell from '../Table/ResultBodyCell';
import {CaretRightOutlined} from '@ant-design/icons';
import {background} from 'plotly.js/src/components/color/attributes';
import JSZip from "jszip";
import './message-lma.css'
import {v4} from "uuid";
import ProjectMD5 from "../../Helpers/ProjectMD5";
import {rehover} from "plotly.js/src/plots/plots";
import LocalService from "../../Service/LocalService";

const labelStyle = {
    width: 250,
};

const inputStyle = {
    paddingRight: 16,
    width: 250,
};

export default function MessageLMA() {
    const type = 'base';
    const {
        appContext,
        dispatchProject,
        selectedHistoryId,
        selection,
        getLMAOfSelectedPlan,
        updateLMAOfSelectedPlan,
        getSelectedProject,
        getSelectedPlan,
    } = useAppContext();

    const {settings, user} = useUser();
    const [loading, setLoading] = useState(false)
    const history = useHistory();

    const getCSList = () => {
        return HecRasHelper.getCSListWithBridge({
            geoFile: selection?.base?.plan?.geometry?.text,
            includeIB: true,
            includeStructure: false,
        }).map((x) => x.name);
    };

    const updateLMA = (name, val) => {
        const lma = getLMAOfSelectedPlan(type);
        if (lma) {
            lma.editedValues[name] = val;
            updateLMAOfSelectedPlan({
                type,
                lma,
                mode: 'edited',
            });
        }
    };

    const getInputData = ({_selection}) => {
        return {
            project: _selection.project.information,
            projection: _selection?.project?.projection,
            geometry: _selection.plan.geometry,
            flow: _selection.plan.flow,
            output: _selection.plan.mappedProfiles ?? [],
            plan: _selection.plan.plan,
            type: _selection.plan.type,
            profileNames: _selection.plan.mapProfiles.map((x) => {
                if (!x.targetName) x.targetName = x.target;

                return x;
            }),
            settings: user.settings,
            projectHash: _selection.projectHash,
            river: _selection.river?.river || null,
            reach: _selection.river?.reach || null,
        };
    };

    const mapProject = (project, filter) => {
        return {
            project: {
                text: project?.project,
                file: 'project.prj'
            },
            plan: {
                file: 'project.p01',
                text: project?.plan?.text,
                name: project?.plan?.short,
                profiles: project?.plan?.profiles,
                version: project?.plan?.version?.replace('.', ''),
            },
            flow: {
                text: project?.flow,
                file: 'project.f01'
            },
            geometry: {
                text: project?.geometry,
                file: 'project.g01'
            },
            filter_output: filter
        }
    }

    const isMatchRS = (cs1, cs2) => {
        cs1 = cs1?.replaceAll(' ', '').toUpperCase()
        cs2 = cs2?.replaceAll(' ', '').toUpperCase()

        const is_multiple_opening = cs1?.includes('MO') || cs2?.includes('MO') || cs1?.includes('#') || cs2?.includes('#');
        if(is_multiple_opening) {
            if(cs1.includes('#') && cs2.includes('#')) {
                return cs1?.replaceAll(' ', '') === cs2?.replaceAll(' ', '')
            }

            cs1 = cs1?.replaceAll('MO', 'BR').replace(/#\d+/, '').toUpperCase()
            cs2 = cs2?.replaceAll('MO', 'BR').replace(/#\d+/, '').toUpperCase()
        }

        return cs1 === cs2
    }

    const getLMAValues = async () => {
        if (loading)
            return

        setLoading(true)
        try {
            const lma = getLMAOfSelectedPlan(type);
            const res = await ApiService.RasCheck.ParseLMA({
                ...getInputData({_selection: selection.base}),
                gageRS: lma?.editedValues?.gageRS,
                minTargetElev: lma?.editedValues?.minTargetElev,
                maxTargetElev: lma?.editedValues?.maxTargetElev,
                numberOfProfileSearch: lma?.numberOfProfileSearch,
                flowMin: lma.flowData?.min,
                flowMax: lma.flowData?.max,
            });

            if (res.success) {
                const data = JSON.parse(res.data);
                const searchProject = data?.data?.flowData?.search_project || {}
                const outputData = await LocalService.run_project(mapProject(searchProject, {}))

                const gageMin = outputData?.result?.profiles?.find(x => isMatchRS(x?.data?.CrossSection, lma?.editedValues?.gageRS) && x?.Profile === 'min')
                const gageMax = outputData?.result?.profiles?.find(x => isMatchRS(x?.data?.CrossSection, lma?.editedValues?.gageRS) && x?.Profile === 'max')

                lma.originalValues = {...data?.data?.LMA};
                lma.editedValues = {...data?.data?.LMA};

                lma.flowTable = data?.data?.flowTable;
                lma.flowData = data?.data?.flowData;
                lma.flowData.search_project.outputBin = outputData?.outputBin;
                lma.flowData.gageMin = gageMin?.data?.WSElev
                lma.flowData.gageMax = gageMax?.data?.WSElev
                lma.prepared = true

                updateLMAOfSelectedPlan({
                    type,
                    lma,
                });
            }
        } catch (exp) {
            console.error(exp)
        }

        setLoading(false)
    };

    const downloadProject = async (project, name) => {
        const zip = new JSZip();
        zip.file(`${project?.project_title}.prj`, project?.project);
        zip.file(`${project?.project_title}.p01`, project?.plan?.text);
        zip.file(`${project?.project_title}.g01`, project?.geometry);
        zip.file(`${project?.project_title}.f01`, project?.flow);

        if(project?.outputBin)
            zip.file(`${project?.project_title}.O01`, project?.outputBin, {base64: true})

        const blob = await zip.generateAsync({type: 'blob', compression: 'DEFLATE', compressionOptions: {level: 9}});

        const element = document.createElement("a");
        element.setAttribute("href", window.URL.createObjectURL(blob));
        element.setAttribute("download", `${project?.project_title}_${name}.zip`);
        element.style.display = "none";
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }

    const saveAsPlan = async () => {
        const _prj = getSelectedProject(type)
        const libraryProject = getLMAOfSelectedPlan(type)?.flowData?.refined?.project

        const _duplicate = JSON.parse(JSON.stringify(getSelectedPlan(type)))
        const _lma = {..._duplicate.LMA}

        delete _duplicate.LMA

        const nextPlan = Math.max(..._prj.projectFiles.map(x => parseInt(x.plan.ext.replaceAll('p', ''))))+1
        const nextFlow = Math.max(..._prj.projectFiles.map(x => parseInt(x.flow.ext.replaceAll('f', ''))))+1
        const nextGeo = Math.max(..._prj.projectFiles.map(x => parseInt(x.geometry.ext.replaceAll('g', ''))))+1

        _duplicate.id = v4()
        _duplicate.profileData = {..._lma.flowData?.refined?.output}
        _duplicate.mapProfiles = [..._lma?.flowData?.refined?.project?.plan?.profiles]
        _duplicate.mappedProfiles = HecRasHelper.mapProfiles(_duplicate.profileData, _duplicate.mapProfiles)
        _duplicate.isFindOutput = 'true'
        _duplicate.plan.id = v4()
        _duplicate.plan.ext = `p${nextPlan.toString().padStart(2, '0')}`
        _duplicate.plan.fileName = `${_duplicate.plan.fileName.split('.')[0]}.${_duplicate.plan.ext}`
        _duplicate.plan.flowFileExt = `f${nextFlow.toString().padStart(2, '0')}`
        _duplicate.plan.geometryFileExt = `g${nextGeo.toString().padStart(2, '0')}`
        _duplicate.plan.title = _duplicate.plan.title.trim() + ' - Library Model'
        _duplicate.plan.text = libraryProject?.plan?.text
            .replace(/Geom File=\S+\s/g, `Geom File=${_duplicate.plan.geometryFileExt}\r`)
            .replace(/Flow File=\S+\s/g, `Flow File=${_duplicate.plan.flowFileExt}\r`)
        _duplicate.plan.shortIdentifier = `Library Model`
        _duplicate.plan.fileName = `${_duplicate.plan.fileName.split('.')[0]}.${_duplicate.plan.ext}`

        _duplicate.geometry.id = v4()
        _duplicate.geometry.ext = _duplicate.plan.geometryFileExt
        _duplicate.geometry.fileName = `${_duplicate.geometry.fileName.split('.')[0]}.${_duplicate.plan.geometryFileExt}`
        _duplicate.geometry.text = libraryProject?.geometry
            .replace(`Geom Title=${_duplicate.geometry.title.trim()}`, `Geom Title=${_duplicate.geometry.title.trim()} - Library Model`)
        _duplicate.geometry.title = _duplicate.geometry.title.trim() + ' - Library Model'

        _duplicate.flow.id = v4()
        _duplicate.flow.ext = _duplicate.plan.flowFileExt
        _duplicate.flow.fileName = `${_duplicate.flow.fileName.split('.')[0]}.${_duplicate.plan.flowFileExt}`
        _duplicate.flow.text = libraryProject?.flow
            .replace(`Flow Title=${_duplicate.flow.title.trim()}`, `Flow Title=${_duplicate.flow.title.trim()} - Library Model`)
        _duplicate.flow.title = _duplicate.flow.title.trim() + ' - Library Model'
        _duplicate.flow.profiles = _duplicate.mapProfiles.map((x, i) => x.select)

        _duplicate.outputBinFile = {
            id: v4(),
            ext: `${_duplicate.plan.ext.replaceAll('p', 'O')}`,
        }

        _duplicate.outputBinFile.fileName = `${_duplicate.plan.fileName.split('.')[0]}.${_duplicate.outputBinFile.ext}`
        _duplicate.outputBinFile.text = libraryProject?.outputBin

        _duplicate.projectHash = ProjectMD5({
            email: user.data.email,
            projectTitle: _prj.information.title,
            flowTitle: _duplicate.flow.title,
            flowFile: _duplicate.flow.fileName,
            geoTitle: _duplicate.geometry.title,
            geoFile: _duplicate.geometry.fileName,
            planTitle: _duplicate.plan.title,
            planFile: _duplicate.plan.fileName
        })


        const text = _prj.information.text.toString()
            .replace(/(Flow File=.+)/g, `$1\r\nFlow File=${_duplicate.flow.ext}`)
            .replace(/(Geom File=.+)/g, `$1\r\nGeom File=${_duplicate.geometry.ext}`)
            .replace(/(Plan File=.+)/g, `$1\r\nPlan File=${_duplicate.plan.ext}`)
        _prj.information.text = text


        _prj.projectFiles.push(_duplicate)
        dispatchProject({
            type: AppActions.SET_PROJECT,
            data: {
                project: _prj
            }
        })

        notification.success({
            message: 'Library Plan was added to project.'
        })
        // console.log(_duplicate, _lma?.flowData?.refined)
    }

    const runLibraryProject = async () => {
        setLoading(true)
        try {
            const lma = getLMAOfSelectedPlan(type)

            let libraryOutput = {}
            if(!lma?.flowData?.refined?.table) {
                libraryOutput = await LocalService.run_project(mapProject(lma?.flowData?.library_project, {
                    river_stations: [lma?.editedValues?.gageRS],
                    river: selection.base?.river?.river || null,
                    reach: selection.base?.river?.reach || null,
                }))
            }

            const data = {
                lma: {
                    targetProfileCount: lma?.editedValues?.numberOfTargetProfiles,
                    targetInterval: lma?.editedValues?.targetElevInterval,
                    libraryFlowItem: lma?.flowData?.library_flow_item,
                    gageRS: lma?.editedValues?.gageRS,
                    flowRS: lma?.flowData?.cs,
                    river: selection.base?.river?.river || null,
                    reach: selection.base?.river?.reach || null,
                    minTargetElev: lma?.editedValues?.minTargetElev,
                    flowTable: lma?.flowTable,
                    flowQTable: lma?.flowData?.refined?.table?.map(x => ({
                        'profile': x.profile,
                        'wsel': x.wsel,
                        'q_w': x.q_w
                    }))
                },
                libraryOutput: {
                    result: libraryOutput.result
                },
                ...lma?.flowData?.library_project
            }
            const output = await ApiService.RasCheck.RunProjectPart1(data)
            const result = JSON.parse(output.data)

            const refinedOutput = await LocalService.run_project(mapProject(result?.refined_project, {}))
            const output2 = await ApiService.RasCheck.RunProjectPart2({
                refined_output: {
                    result: refinedOutput?.result
                },
                refined_table: result?.refined_table,
                lma: {
                    gageRS: lma?.editedValues?.gageRS,
                }
            })

            const refined_result = JSON.parse(output2?.data || '{}')
            lma.flowData.refined = {
                table: refined_result?.refined_table,
                project: result?.refined_project,
                output: refined_result.refined_output,
            }
            lma.flowData.library_project.outputBin = libraryOutput?.outputBin
            lma.flowData.refined.project.outputBin = refinedOutput?.outputBin

            updateLMAOfSelectedPlan({type, data: lma})
        } catch (exp) {
            console.log(exp)
        }
        setLoading(false)
    }

    const libWsel = (wsel, libWsel) => {
        if(Math.abs(wsel-libWsel) === 0) {
            return <div>{libWsel?.toFixed(2)}</div>
        }
        else if(Math.abs(wsel-libWsel) <= settings?.defaults?.tnm_tolerance) {
            return <div style={{color: 'orange'}}>{libWsel?.toFixed(2)}</div>
        }
        else {
            return <div style={{color: 'red'}}>{libWsel?.toFixed(2)}</div>
        }
    }

    useEffect(() => {
        const lma = getLMAOfSelectedPlan(type);
        if (lma.prevGageRS !== lma.editedValues.gageRS) {
            lma.prepared = false
            lma.prevGageRS = lma.editedValues.gageRS
            updateLMAOfSelectedPlan({
                type,
                lma,
            });
        }
    }, [getLMAOfSelectedPlan(type)?.editedValues?.gageRS]);


    return (
        <div>
            <Popover
                placement={'bottomLeft'}
                trigger={'click'}
                content={
                    <PopoverScrollContent maxHeight={600}>
                        <SelectPlanV2 type={type}/>
                    </PopoverScrollContent>
                }
            >
                <GrayButton
                    style={{
                        fontSize: '14pt',
                        width: '100%',
                        marginBottom: 16,
                        height: 40,
                    }}
                >
                    <b style={{marginRight: 4}}>
                        {selection.base.project?.information?.title?.trim()}:
                    </b>{' '}
                    {selection.base.plan?.type}{' '}
                    {selection.base.river &&
                        `, ${selection.base.river?.river} - ${selection.base.river?.river}`}
                </GrayButton>
            </Popover>
            <br/>
            <ProjectInfo
                autoHeight={true}
                showNotes={true}
                folderName={selection?.base.project?.information?.folderName}
                title={selection?.base.project?.information?.title}
                descriptions={
                    selection?.base.project?.information?.descriptions
                }
                planTitle={selection?.base.plan?.plan?.title}
                version={
                    selection?.base.project?.projectFiles &&
                    selection.base.project?.projectFiles?.length > 0 &&
                    selection?.base.plan?.plan?.version
                }
            />
            <Divider/>
            <Typography.Title level={5}>
                Step 1: Identify the Target Elevation Range
            </Typography.Title>
            <table>
                <tbody>
                <tr>
                    <td style={labelStyle}>
                        Gage River Stations
                        <b style={{color: 'red'}}> *</b>
                    </td>
                    <td style={inputStyle}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                            }}
                        >
                            <ResultListBox
                                list={getCSList()}
                                suffixIcon={'Select Gage RS'}
                                value={
                                    getLMAOfSelectedPlan(type)?.editedValues?.gageRS
                                }
                                onChange={(cs) => updateLMA('gageRS', cs)}
                            />
                            {
                                !getLMAOfSelectedPlan(type)?.prepared && (
                                    <Button
                                        loading={loading}
                                        size={'small'}
                                        type={'primary'}
                                        onClick={getLMAValues}
                                        style={{}}
                                    >
                                        Prepare
                                    </Button>
                                )
                            }
                        </div>
                    </td>
                </tr>
                <br/>
                {
                    getLMAOfSelectedPlan(type)?.prepared && (
                        <>
                            <tr>
                                <td style={labelStyle}>Minimum Bank Elevation (ft)</td>
                                <td style={inputStyle}>
                                    <InputNumber
                                        readOnly={true}
                                        style={{width: '100%', backgroundColor: '#f3f3f3'}}
                                        value={
                                            getLMAOfSelectedPlan(type)?.editedValues
                                                ?.minBankElev
                                        }
                                        onChange={(val) =>
                                            updateLMA('minBankElev', val)
                                        }
                                        size='small'
                                    />
                                </td>
                                <td style={labelStyle}>
                                    Minimum Target Elevation (ft)
                                </td>
                                <td style={inputStyle}>
                                    <InputNumber
                                        style={{width: '100%', paddingLeft: 0}}
                                        value={
                                            getLMAOfSelectedPlan(type)?.editedValues
                                                ?.minTargetElev
                                        }
                                        onChange={(val) => {
                                            const lma = getLMAOfSelectedPlan(type)
                                            lma.editedValues.minTargetElev = val
                                            lma.editedValues.numberOfTargetProfiles = (lma.editedValues.maxTargetElev - lma.editedValues.minTargetElev) * 2 + 1
                                            updateLMAOfSelectedPlan({type, data: lma})
                                        }}
                                        size='small'
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={labelStyle}>100yr WSEL (ft)</td>
                                <td style={inputStyle}>
                                    <InputNumber
                                        readOnly={true}
                                        style={{width: '100%', backgroundColor: '#f3f3f3'}}
                                        value={
                                            getLMAOfSelectedPlan(type)?.editedValues
                                                ?.yr100WSEL
                                        }
                                        onChange={(val) => updateLMA('maxTargetElev', val)}
                                        size='small'
                                    />
                                </td>
                                <td style={labelStyle}>
                                    Maximum Target Elevation (ft)
                                </td>
                                <td style={inputStyle}>
                                    <InputNumber
                                        style={{width: '100%'}}
                                        value={
                                            getLMAOfSelectedPlan(type)?.editedValues
                                                ?.maxTargetElev
                                        }
                                        onChange={(val) => {
                                            const lma = getLMAOfSelectedPlan(type)
                                            lma.editedValues.maxTargetElev = val
                                            lma.editedValues.numberOfTargetProfiles = (lma.editedValues.maxTargetElev - lma.editedValues.minTargetElev) * 2 + 1
                                            updateLMAOfSelectedPlan({type, data: lma})
                                        }}
                                        size='small'
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={labelStyle}>500yr WSEL (ft)</td>
                                <td style={inputStyle}>
                                    <InputNumber
                                        readOnly={true}
                                        style={{width: '100%', backgroundColor: '#f3f3f3'}}
                                        value={
                                            getLMAOfSelectedPlan(type)?.editedValues
                                                ?.yr500WSEL
                                        }
                                        onChange={(val) => updateLMA('yr500WSEL', val)}
                                        size='small'
                                    />
                                </td>
                                <td style={labelStyle}>
                                    Target Elevation Interval (ft)
                                </td>
                                <td style={inputStyle}>
                                    <InputNumber
                                        readOnly={true}
                                        style={{width: '100%', backgroundColor: '#f3f3f3'}}
                                        value={
                                            getLMAOfSelectedPlan(type)?.editedValues
                                                ?.targetElevInterval
                                        }
                                        onChange={(val) =>
                                            updateLMA('targetElevInterval', val)
                                        }
                                        size='small'
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={labelStyle}>
                                    Maximum High Chord Elevation (ft)
                                </td>
                                <td style={inputStyle}>
                                    <InputNumber
                                        readOnly={true}
                                        style={{width: '100%', backgroundColor: '#f3f3f3'}}
                                        value={
                                            getLMAOfSelectedPlan(type)?.editedValues
                                                ?.maxHighChord
                                        }
                                        onChange={(val) =>
                                            updateLMA('maxHighChord', val)
                                        }
                                        size='small'
                                    />
                                </td>
                                <td style={labelStyle}>Number of Target Profiles</td>
                                <td style={inputStyle}>
                                    <InputNumber
                                        readOnly={true}
                                        style={{width: '100%', backgroundColor: '#f3f3f3'}}
                                        value={
                                            getLMAOfSelectedPlan(type)?.editedValues
                                                ?.numberOfTargetProfiles
                                        }
                                        onChange={(val) =>
                                            updateLMA('numberOfTargetProfiles', val)
                                        }
                                        size='small'
                                    />
                                </td>
                            </tr>
                        </>
                    )
                }
                </tbody>
            </table>
            {
                getLMAOfSelectedPlan(type)?.prepared && (
                    <>
                        <Divider/>
                        <Typography.Title level={5}>
                            Step 2: Populate the Search Flow Table and Flow File
                        </Typography.Title>
                        <table>
                            <tbody>
                            <tr>
                                <td style={{width: HecRasHelper.getWidth(50)}}>
                                    <table>
                                        <tbody>
                                        <tr>
                                            <td>
                                                <Typography.Text
                                                    style={{}}
                                                >
                                                    Nearest US Flow Change RS
                                                </Typography.Text>
                                            </td>
                                            <td style={{paddingLeft: 8}}>
                                                <Input
                                                    value={
                                                        getLMAOfSelectedPlan(type)
                                                            ?.flowData?.cs
                                                    }
                                                    size='small'
                                                    readOnly={true}
                                                    style={{backgroundColor: '#f3f3f3'}}
                                                />
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </td>
                                <td></td>
                                <td></td>
                            </tr>
                            <br/>
                            <tr>
                                <td colSpan={3}>
                                    <Typography.Title
                                        level={5}
                                        style={{fontSize: '10pt'}}
                                    >
                                        Flow Change Multiplier Table
                                    </Typography.Title>
                                </td>
                            </tr>
                            <tr>
                                <td
                                    colSpan={2}
                                    style={{width: HecRasHelper.getWidth(50)}}
                                >
                                    <ResultTable scrollable={{height: 300}}>
                                        <ResultHeader>
                                            <ResultRow>
                                                <ResultHeaderCell
                                                    scrollable={{top: -1}}
                                                    style={{
                                                        width: HecRasHelper.getWidth(
                                                            15
                                                        ),
                                                        textAlign: 'start',
                                                    }}
                                                >
                                                    River Station
                                                </ResultHeaderCell>
                                                <ResultHeaderCell
                                                    scrollable={{top: -1}}
                                                    style={{
                                                        width: HecRasHelper.getWidth(
                                                            10
                                                        ),
                                                        textAlign: 'end',
                                                    }}
                                                >
                                                    100-year
                                                </ResultHeaderCell>
                                                <ResultHeaderCell
                                                    scrollable={{top: -1}}
                                                    style={{
                                                        width: HecRasHelper.getWidth(
                                                            10
                                                        ),
                                                        textAlign: 'end',
                                                    }}
                                                >
                                                    500-year
                                                </ResultHeaderCell>
                                                <ResultHeaderCell
                                                    scrollable={{top: -1}}
                                                    style={{
                                                        width: HecRasHelper.getWidth(
                                                            10
                                                        ),
                                                        textAlign: 'end',
                                                    }}
                                                >
                                                    Multiplier
                                                </ResultHeaderCell>
                                            </ResultRow>
                                        </ResultHeader>
                                        <ResultBody>
                                            {getLMAOfSelectedPlan(type)?.flowTable?.map(
                                                (fl) => (
                                                    <ResultRow>
                                                        <ResultBodyCell
                                                            style={{
                                                                width: HecRasHelper.getWidth(
                                                                    15
                                                                ),
                                                                textAlign: 'start',
                                                                backgroundColor:
                                                                    fl?.cs ===
                                                                    getLMAOfSelectedPlan(
                                                                        type
                                                                    )?.flowData?.cs
                                                                        ? 'orange'
                                                                        : '',
                                                            }}
                                                        >
                                                            {fl?.cs}
                                                        </ResultBodyCell>
                                                        <ResultBodyCell
                                                            style={{
                                                                width: HecRasHelper.getWidth(
                                                                    10
                                                                ),
                                                                textAlign: 'end',
                                                                backgroundColor:
                                                                    fl?.cs ===
                                                                    getLMAOfSelectedPlan(
                                                                        type
                                                                    )?.flowData?.cs
                                                                        ? 'orange'
                                                                        : '',
                                                            }}
                                                        >
                                                            {fl?.yr100?.toFixed(2)}
                                                        </ResultBodyCell>
                                                        <ResultBodyCell
                                                            style={{
                                                                width: HecRasHelper.getWidth(
                                                                    10
                                                                ),
                                                                textAlign: 'end',
                                                                backgroundColor:
                                                                    fl?.cs ===
                                                                    getLMAOfSelectedPlan(
                                                                        type
                                                                    )?.flowData?.cs
                                                                        ? 'orange'
                                                                        : '',
                                                            }}
                                                        >
                                                            {fl?.yr500?.toFixed(2)}
                                                        </ResultBodyCell>
                                                        <ResultBodyCell
                                                            style={{
                                                                width: HecRasHelper.getWidth(
                                                                    10
                                                                ),
                                                                textAlign: 'end',
                                                                backgroundColor:
                                                                    fl?.cs ===
                                                                    getLMAOfSelectedPlan(
                                                                        type
                                                                    )?.flowData?.cs
                                                                        ? 'orange'
                                                                        : '',
                                                            }}
                                                        >
                                                            {fl?.multiplier?.toFixed(3)}
                                                        </ResultBodyCell>
                                                    </ResultRow>
                                                )
                                            )}
                                        </ResultBody>
                                    </ResultTable>
                                </td>
                                <td
                                    style={{
                                        paddingLeft: 12,
                                        verticalAlign: 'top',
                                    }}
                                >
                                    <table>
                                        <tbody>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <Typography.Title
                                                    level={5}
                                                    style={{
                                                        fontSize: '10pt',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Flow (cfs)
                                                </Typography.Title>
                                            </td>
                                            <td></td>
                                            <td>
                                                <Typography.Title
                                                    level={5}
                                                    style={{
                                                        fontSize: '10pt',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    WSEL at Gage RS (ft)
                                                </Typography.Title>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{width: 70}}>Minimum</td>
                                            <td>
                                                <InputNumber
                                                    style={{width: 100}}
                                                    value={
                                                        getLMAOfSelectedPlan(type)
                                                            ?.flowData?.min
                                                    }
                                                    onChange={(val) => {
                                                        const lma = getLMAOfSelectedPlan(type);
                                                        if (lma) {
                                                            lma.flowData.min = val;
                                                            updateLMAOfSelectedPlan({
                                                                type,
                                                                lma,
                                                            });
                                                        }
                                                    }}
                                                    size='small'
                                                />
                                            </td>
                                            <td rowSpan={2}>
                                                <Button
                                                    size={'small'}
                                                    type={'primary'}
                                                    loading={loading}
                                                    icon={<CaretRightOutlined/>}
                                                    onClick={getLMAValues}
                                                    style={{
                                                        marginRight: 8,
                                                        marginLeft: 8,
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <InputNumber
                                                    readOnly={true}
                                                    style={{
                                                        width: 100,
                                                        color: getLMAOfSelectedPlan(type)?.editedValues?.minTargetElev < getLMAOfSelectedPlan(type)?.flowData?.gageMin ? 'red' : '',
                                                        backgroundColor: '#f3f3f3'
                                                    }}
                                                    value={
                                                        getLMAOfSelectedPlan(type)
                                                            ?.flowData?.gageMin
                                                    }
                                                    size='small'
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{width: 70}}>Maximum</td>
                                            <td>
                                                <InputNumber
                                                    style={{width: 100}}
                                                    value={
                                                        getLMAOfSelectedPlan(type)
                                                            ?.flowData?.max
                                                    }
                                                    onChange={(val) => {
                                                        const lma = getLMAOfSelectedPlan(type);
                                                        if (lma) {
                                                            lma.flowData.max = val;
                                                            updateLMAOfSelectedPlan({
                                                                type,
                                                                lma,
                                                            });
                                                        }
                                                    }}
                                                    size='small'
                                                />
                                            </td>
                                            <td>
                                                <InputNumber
                                                    readOnly={true}
                                                    style={{
                                                        width: 100,
                                                        color: getLMAOfSelectedPlan(type)?.editedValues?.maxTargetElev > getLMAOfSelectedPlan(type)?.flowData?.gageMax ? 'red' : '',
                                                        backgroundColor: '#f3f3f3'
                                                    }}
                                                    value={
                                                        getLMAOfSelectedPlan(type)
                                                            ?.flowData?.gageMax
                                                    }
                                                    onChange={0}
                                                    size='small'
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{width: 180}}>
                                                Number of Search Profiles
                                            </td>
                                            <td colSpan={3}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <InputNumber
                                                        style={{width: 244, margin: 0}}
                                                        value={
                                                            getLMAOfSelectedPlan(type)
                                                                ?.numberOfProfileSearch
                                                        }
                                                        onChange={(val) => {
                                                            const lma = getLMAOfSelectedPlan(type);
                                                            if (lma) {
                                                                lma.numberOfProfileSearch = val;
                                                                updateLMAOfSelectedPlan({
                                                                    type,
                                                                    lma,
                                                                });
                                                            }
                                                        }}
                                                        size='small'
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td colSpan={3}>
                                                <Button
                                                    size={'small'}
                                                    onClick={() => downloadProject(getLMAOfSelectedPlan(type)?.flowData?.search_project, 'limit')}
                                                >
                                                    Download Limit Project
                                                </Button>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                        <Divider/>
                        <Typography.Title level={5}>
                            Step 3: Run the Search Plan using the Search Flow File
                        </Typography.Title>
                        <div
                            style={{display: 'flex', alignItems: 'center', gap: 8}}
                        >
                            <Button
                                size={'small'}
                                type={'primary'}
                                loading={loading}
                                icon={<CaretRightOutlined/>}
                                onClick={runLibraryProject}
                            />
                            <Button
                                size={'small'}
                                onClick={() => downloadProject(getLMAOfSelectedPlan(type)?.flowData?.library_project, 'search')}
                            >
                                Download Search Project
                            </Button>
                        </div>
                        <br/>
                        {
                            getLMAOfSelectedPlan(type)?.flowData?.refined?.table?.length > 0 && (
                                <>
                                    <ResultTable>
                                        <ResultHeader>
                                            <ResultHeaderCell
                                                style={{width: HecRasHelper.getWidth(10), textAlign: 'end'}}>WSEL</ResultHeaderCell>
                                            <ResultHeaderCell style={{width: HecRasHelper.getWidth(10), textAlign: 'end'}}>Q
                                                (cfs)</ResultHeaderCell>
                                            <ResultHeaderCell style={{width: HecRasHelper.getWidth(10), textAlign: 'end'}}>Target
                                                WSEL</ResultHeaderCell>
                                        </ResultHeader>
                                        <ResultBody>
                                            {
                                                getLMAOfSelectedPlan(type)?.flowData?.refined?.table?.map((item, idx) => (
                                                    <ResultRow key={idx.toString()}>
                                                        <ResultBodyCell
                                                            style={{textAlign: 'end'}}>{item?.wsel?.toFixed(2)}</ResultBodyCell>
                                                        <ResultBodyCell style={{textAlign: 'end'}}>
                                                            <div className={'input-div-lma'}>
                                                                <InputNumber
                                                                    size={'small'}
                                                                    value={item?.q_w}
                                                                    controls={false}
                                                                    precision={2}
                                                                    onChange={(val) => {
                                                                        const lma = getLMAOfSelectedPlan(type)
                                                                        lma?.flowData?.refined?.table?.forEach((x, idx) => {
                                                                            if (x?.wsel === item?.wsel) {
                                                                                x.q_w = val
                                                                            }
                                                                        })
                                                                        updateLMAOfSelectedPlan({type, data: lma})
                                                                    }}
                                                                />
                                                            </div>
                                                        </ResultBodyCell>
                                                        <ResultBodyCell
                                                            style={{textAlign: 'end'}}>
                                                            {libWsel(item?.wsel, item?.wsel_target)}
                                                        </ResultBodyCell>
                                                    </ResultRow>
                                                ))
                                            }
                                        </ResultBody>
                                    </ResultTable>
                                    <Space>
                                        <Button
                                            type={'primary'}
                                            size={'small'}
                                            onClick={() => downloadProject(getLMAOfSelectedPlan(type)?.flowData?.refined?.project, 'library')}
                                        >
                                            Download Library Project
                                        </Button>
                                        <Button
                                            type={'primary'}
                                            size={'small'}
                                            onClick={() =>  saveAsPlan()}
                                        >
                                            Save As Plan
                                        </Button>
                                    </Space>
                                </>
                            )
                        }
                    </>
                )
            }
        </div>
    );
}
